import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { TrendingDown, Zap, AlertTriangle, Gauge } from "lucide-react";

const EVENT_META = {
  harsh_brake:   { color: "#f59e0b", label: "Harsh Brake",   icon: TrendingDown },
  rapid_accel:   { color: "#fb923c", label: "Rapid Accel",   icon: Zap },
  sharp_turn:    { color: "#ef4444", label: "Sharp Turn",    icon: AlertTriangle },
  extreme_speed: { color: "#dc2626", label: "Extreme Speed", icon: Gauge },
};

export default function DrivingHeatmap({ days = 30 }) {
  const [data, setData] = useState({ events: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (filter !== "all") params.append("event_type", filter);
      const { data } = await API.get(`/management/driving-heatmap?${params}`);
      setData(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [days, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Compute map center
  const center = (() => {
    if (data.events.length === 0) return [32.78, -96.80]; // Dallas
    const valid = data.events.filter(e => e.latitude && e.longitude);
    if (valid.length === 0) return [32.78, -96.80];
    const lat = valid.reduce((a, e) => a + e.latitude, 0) / valid.length;
    const lng = valid.reduce((a, e) => a + e.longitude, 0) / valid.length;
    return [lat, lng];
  })();

  return (
    <div className="space-y-3" data-testid="driving-heatmap">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex flex-wrap gap-1.5">
          {["all", ...Object.keys(EVENT_META)].map(k => {
            const meta = EVENT_META[k];
            const count = k === "all" ? data.total : (data.counts[k] || 0);
            const active = filter === k;
            return (
              <button key={k} onClick={() => setFilter(k)}
                data-testid={`heatmap-filter-${k}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                  active ? "bg-zinc-900 text-white border-zinc-900"
                         : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
                }`}>
                {k === "all" ? "All Events" : meta.label} ({count})
              </button>
            );
          })}
        </div>
        <span className="text-xs text-zinc-500">
          {loading ? "Loading…" : `${data.events.length} events · last ${days} days`}
        </span>
      </div>

      <div className="rounded-xl border border-zinc-200 overflow-hidden bg-zinc-100" style={{ height: 500 }}
        data-testid="heatmap-map">
        <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>' />
          {data.events.filter(e => e.latitude && e.longitude).map(e => {
            const meta = EVENT_META[e.type] || EVENT_META.harsh_brake;
            return (
              <CircleMarker
                key={e.id}
                center={[e.latitude, e.longitude]}
                radius={10}
                pathOptions={{
                  color: meta.color,
                  fillColor: meta.color,
                  fillOpacity: 0.5,
                  weight: 2,
                }}
              >
                <Popup>
                  <div className="text-xs">
                    <p className="font-bold">{meta.label}</p>
                    <p>{e.vendor_name}</p>
                    {e.type === "harsh_brake" && <p>{e.from_speed} → {e.to_speed} mph (drop {e.drop})</p>}
                    {e.type === "rapid_accel" && <p>{e.from_speed} → {e.to_speed} mph (+{e.rise})</p>}
                    {e.type === "sharp_turn" && <p>{e.heading_change}° at {e.speed} mph</p>}
                    {e.type === "extreme_speed" && <p>{e.speed} mph</p>}
                    <p className="text-zinc-500">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-[11px] text-zinc-600">
        {Object.entries(EVENT_META).map(([k, m]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full"
              style={{ background: m.color, opacity: 0.55, border: `2px solid ${m.color}` }} />
            {m.label}
          </span>
        ))}
      </div>
    </div>
  );
}
